import { useEffect, useMemo, useState } from 'react'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import {
  deriveCollaborationState,
  type Collaboration as DerivedCollaboration,
} from '@adthlete/common/utils/collaborationState'
import { getAthleteRouteForMilestone } from '@adthlete/common/utils/milestoneConfig'
import { useCollaborations } from '@/hooks/useCollaborations'
import type { Collaboration, Milestone } from '@/redux/collaborationsApi'
import athleteApi from '@/lib/api'
import { typeConfig, type FeedItem, type FeedItemType } from '@/types/homeFeed'

interface BackendTodo {
  id: string
  title: string
  completed: boolean
  priority?: string
  actionLink?: string
  type:
    'setup' | 'connection' | 'collaboration' | 'creative_suggestion' | 'video_suggestion' | string
}

interface NextUp {
  brandName: string
  date: string
}

const PAYMENT_RECENT_WINDOW_DAYS = 7

const formatCurrency = (amount: number | null | undefined): string => {
  if (!amount) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatRelativeDue = (dueDate: string): string => {
  const days = differenceInCalendarDays(parseISO(dueDate), new Date())
  if (days < 0) {
    const abs = Math.abs(days)
    return `${abs} day${abs === 1 ? '' : 's'} overdue`
  }
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  return `Due in ${days} days`
}

const milestoneRoute = (collabId: string, milestone: Milestone | null | undefined): string => {
  if (milestone?.name) {
    const specific = getAthleteRouteForMilestone(milestone.name, collabId)
    if (specific) return specific
  }
  return `/collaborations/${collabId}`
}

/**
 * Build a FeedItem with `priority` auto-filled from typeConfig — the type's
 * priority is the source of truth, so push sites can't drift.
 */
const makeItem = (type: FeedItemType, rest: Omit<FeedItem, 'type' | 'priority'>): FeedItem => ({
  type,
  priority: typeConfig[type].priority,
  ...rest,
})

const todoToFeedItem = (todo: BackendTodo): FeedItem | null => {
  if (todo.type === 'setup') {
    return makeItem('setup', {
      id: todo.id,
      title: todo.title,
      subtitle: 'Helps brands book you',
      ctaLabel: 'Complete',
      ctaPath: todo.actionLink ?? '/brand-view',
      brandName: '',
    })
  }
  if (todo.type === 'connection') {
    return makeItem('manager_invite', {
      id: todo.id,
      title: todo.title,
      subtitle: 'Accept or decline the invitation',
      ctaLabel: 'Review',
      ctaPath: todo.actionLink ?? '/',
      brandName: '',
    })
  }
  if (todo.type === 'creative_suggestion') {
    return makeItem('creative_suggestion', {
      id: todo.id,
      title: todo.title,
      subtitle: 'Open and consider before submitting your concept',
      ctaLabel: 'Review',
      ctaPath: todo.actionLink ?? '/collaborations',
      brandName: '',
    })
  }
  if (todo.type === 'video_suggestion') {
    return makeItem('video_suggestion', {
      id: todo.id,
      title: todo.title,
      subtitle: 'Open and decide whether to use it',
      ctaLabel: 'Review',
      ctaPath: todo.actionLink ?? '/collaborations',
      brandName: '',
    })
  }
  // 'collaboration' rolls up offers + active collabs server-side; we derive
  // richer per-collab cards below, so skip these to avoid duplicates.
  return null
}

/** What the brand is reviewing, in words: "your video", not the step's name ("your prepare") */
const REVIEW_NOUN: Record<string, string> = {
  submit_creative_concept: 'concept',
  submit_video: 'video',
  post_content: 'post',
  create_draft: 'draft',
  prepare_appearance: 'appearance plan',
  check_in: 'check-in',
  collaboration_live: 'live post',
  content_live: 'live post',
  license_content: 'ad codes',
  confirm_received: 'delivery confirmation',
}

const reviewingTitle = (brandName: string, m: Milestone) => {
  const noun = m.slug ? REVIEW_NOUN[m.slug] : undefined
  return noun
    ? `${brandName} is reviewing your ${noun}`
    : `With ${brandName} to review: ${m.name || 'your submission'}`
}

/**
 * Convert one collaboration into at most one FeedItem. Returns null when the
 * collab has nothing actionable for the athlete (rejected, expired, completed
 * without a recent payment, no current milestone, brand-side action, etc.).
 *
 * The order of checks here is the rule: each `if` is a bucket, and the first
 * match wins. To add a new card type, add a new `if` and a corresponding entry
 * in typeConfig — the priority field there controls where it lands in the feed.
 */
const collabToFeedItem = (collab: Collaboration, now: Date): FeedItem | null => {
  // The athlete archived it: keep it off Home
  if (collab.archived) return null

  const brandName = collab.brand?.name ?? 'Brand'
  const collabId = collab.id

  // 1. New deal offers.
  if (collab.status === 'offered') {
    const amountStr = collab.paymentAmount ? ` — ${formatCurrency(collab.paymentAmount)}` : ''
    return makeItem('offer', {
      id: `offer-${collabId}`,
      title: `${brandName} sent you a deal offer${amountStr}`,
      subtitle: collab.title || 'Review terms and respond',
      ctaLabel: 'Review offer',
      ctaPath: `/bids/${collabId}`,
      brandName,
      sortDate: collab.bidReceivedAt,
    })
  }

  // Dead-end statuses — nothing to surface.
  if (['rejected', 'expired', 'withdrawn'].includes(collab.status)) return null

  const state = deriveCollaborationState(collab as unknown as DerivedCollaboration)
  const m = state.currentMilestone as unknown as Milestone | null

  // 2. Recent payment on a completed collab.
  if (state.isComplete) {
    if (collab.paymentStatus === 'paid' && collab.paymentDate && collab.paymentAmount) {
      const days = differenceInCalendarDays(now, parseISO(collab.paymentDate))
      if (days >= 0 && days <= PAYMENT_RECENT_WINDOW_DAYS) {
        return makeItem('payment_received', {
          id: `pay-${collabId}`,
          title: `${brandName} paid ${formatCurrency(collab.paymentAmount)}`,
          subtitle: days === 0 ? 'Just received' : `Received ${days}d ago`,
          ctaLabel: 'View',
          ctaPath: '/earnings',
          brandName,
          sortDate: collab.paymentDate,
        })
      }
    }
    return null
  }

  if (!m) return null
  const route = milestoneRoute(collabId, m)
  const milestoneName = m.name || 'Deliverable'
  const days = m.dueDate ? differenceInCalendarDays(parseISO(m.dueDate), now) : null

  // 3. Athlete already submitted; brand is reviewing.
  if (state.actionOwner === 'marketer') {
    return makeItem('pending_approval', {
      id: `pending-${collabId}-${m.id}`,
      title: reviewingTitle(brandName, m),
      subtitle: 'Submitted · awaiting sign-off',
      ctaLabel: 'View',
      ctaPath: route,
      brandName,
      sortDate: m.dueDate,
    })
  }

  // From here on, only items the athlete owns.
  if (state.actionOwner !== 'athlete') return null

  // 4. Revision requested — escalate above due-date bucketing.
  if (m.status === 'revision_requested') {
    return makeItem('revision', {
      id: `rev-${collabId}-${m.id}`,
      title: `${brandName} requested a revision`,
      subtitle: `${milestoneName} — open feedback and resubmit`,
      ctaLabel: 'View feedback',
      ctaPath: route,
      brandName,
      sortDate: m.dueDate,
    })
  }

  // 5. Actionable but undated — surface as upcoming so the athlete still sees it.
  if (days === null) {
    return makeItem('upcoming', {
      id: `up-${collabId}-${m.id}`,
      title: `${brandName} — ${milestoneName}`,
      subtitle: 'Ready when you are',
      ctaLabel: 'Open',
      ctaPath: route,
      brandName,
    })
  }

  // 6. Date-driven buckets.
  if (days < 0) {
    return makeItem('overdue', {
      id: `over-${collabId}-${m.id}`,
      title: `${brandName} — ${milestoneName}`,
      subtitle: formatRelativeDue(m.dueDate!),
      ctaLabel: 'Fix now',
      ctaPath: route,
      brandName,
      sortDate: m.dueDate,
    })
  }
  if (days <= 3) {
    return makeItem('due_soon', {
      id: `soon-${collabId}-${m.id}`,
      title: `${brandName} — ${milestoneName}`,
      subtitle: formatRelativeDue(m.dueDate!),
      ctaLabel: 'Get started',
      ctaPath: route,
      brandName,
      sortDate: m.dueDate,
    })
  }
  if (days <= 7) {
    return makeItem('upcoming', {
      id: `up-${collabId}-${m.id}`,
      title: `${brandName} — ${milestoneName}`,
      subtitle: formatRelativeDue(m.dueDate!),
      ctaLabel: 'Preview',
      ctaPath: route,
      brandName,
      sortDate: m.dueDate,
    })
  }

  // Beyond 7 days out: don't surface yet — keeps the feed focused on what's actually next.
  return null
}

const compareFeedItems = (a: FeedItem, b: FeedItem): number => {
  if (a.priority !== b.priority) return a.priority - b.priority
  const aTime = a.sortDate ? new Date(a.sortDate).getTime() : Number.POSITIVE_INFINITY
  const bTime = b.sortDate ? new Date(b.sortDate).getTime() : Number.POSITIVE_INFINITY
  return aTime - bTime
}

const findNextUp = (collaborations: Collaboration[]): NextUp | null => {
  const now = new Date()
  let soonest: NextUp | null = null
  for (const collab of collaborations) {
    if (
      collab.archived ||
      collab.status === 'offered' ||
      collab.status === 'rejected' ||
      collab.status === 'expired' ||
      collab.status === 'withdrawn'
    ) {
      continue
    }
    for (const m of collab.milestones ?? []) {
      if (!m.dueDate) continue
      if (m.status === 'approved' || m.status === 'completed') continue
      const days = differenceInCalendarDays(parseISO(m.dueDate), now)
      if (days < 0) continue
      if (!soonest || parseISO(m.dueDate) < parseISO(soonest.date)) {
        soonest = { brandName: collab.brand?.name ?? 'Brand', date: m.dueDate }
      }
    }
  }
  return soonest
}

const isItem = (x: FeedItem | null): x is FeedItem => x !== null

export interface UseHomeFeedItemsResult {
  items: FeedItem[]
  nextUp: NextUp | null
  isLoading: boolean
}

export function useHomeFeedItems(): UseHomeFeedItemsResult {
  const { collaborations, isLoading: collabsLoading } = useCollaborations()

  const [todos, setTodos] = useState<BackendTodo[]>([])
  const [todosLoading, setTodosLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const fetchTodos = async () => {
      try {
        const res = await athleteApi.get<{ todos: BackendTodo[] }>('/athlete/todos')
        if (!cancelled) setTodos(res.data.todos ?? [])
      } catch (err) {
        console.error('Error fetching athlete todos:', err)
      } finally {
        if (!cancelled) setTodosLoading(false)
      }
    }
    fetchTodos()
    return () => {
      cancelled = true
    }
  }, [])

  const items = useMemo<FeedItem[]>(() => {
    const now = new Date()
    return [
      ...todos.map(todoToFeedItem).filter(isItem),
      ...collaborations.map((c) => collabToFeedItem(c, now)).filter(isItem),
    ].sort(compareFeedItems)
  }, [collaborations, todos])

  const nextUp = useMemo(() => findNextUp(collaborations), [collaborations])

  return { items, nextUp, isLoading: collabsLoading || todosLoading }
}
